import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Check,
  X,
  CreditCard,
  Zap,
  Crown,
  Shield,
  Loader2,
} from 'lucide-react';
import { subscriptionApi } from './api';

const PLAN_DEFS = [
  {
    id: 'free',
    name: 'Free',
    price: 0,
    icon: Shield,
    description: 'Perfect for getting started',
    features: {
      campaigns: '2 campaigns',
      players: '4 players per campaign',
      storage: '100 MB storage',
      maps: 'Basic map tools',
      combat: 'Combat tracker',
      ai: false,
      priority_support: false,
      custom_themes: false,
      advanced_maps: false,
      api_access: false,
    },
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 9.99,
    icon: Zap,
    popular: true,
    description: 'For dedicated game masters',
    features: {
      campaigns: 'Unlimited campaigns',
      players: '10 players per campaign',
      storage: '5 GB storage',
      maps: 'Advanced map tools',
      combat: 'Combat tracker + initiative',
      ai: 'AI assistant',
      priority_support: 'Priority support',
      custom_themes: 'Custom themes',
      advanced_maps: false,
      api_access: false,
    },
  },
  {
    id: 'master',
    name: 'Master',
    price: 19.99,
    icon: Crown,
    description: 'The ultimate RPG toolkit',
    features: {
      campaigns: 'Unlimited campaigns',
      players: 'Unlimited players',
      storage: '50 GB storage',
      maps: 'Advanced map tools',
      combat: 'Combat tracker + initiative',
      ai: 'AI assistant (enhanced)',
      priority_support: 'Priority support',
      custom_themes: 'Custom themes',
      advanced_maps: 'Advanced maps & 3D',
      api_access: 'API access',
    },
  },
];

const FEATURE_LABELS = {
  campaigns: 'Campaigns',
  players: 'Players per campaign',
  storage: 'Storage',
  maps: 'Map tools',
  combat: 'Combat tracker',
  ai: 'AI Assistant',
  priority_support: 'Priority support',
  custom_themes: 'Custom themes',
  advanced_maps: 'Advanced maps',
  api_access: 'API access',
};

const PLAN_ORDER = { free: 0, pro: 1, master: 2 };

export default function PricingPage() {
  const navigate = useNavigate();
  const [currentPlan, setCurrentPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [providerDialogOpen, setProviderDialogOpen] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }
    subscriptionApi.getCurrentSubscription()
      .then((data) => setCurrentPlan(data.plan || 'free'))
      .catch(() => setCurrentPlan('free'))
      .finally(() => setLoading(false));
  }, []);

  const handleSelectPlan = (planId) => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/register');
      return;
    }

    if (planId === 'free') {
      navigate('/subscription');
      return;
    }

    if (planId === currentPlan) return;

    // If changing from a paid plan to another paid plan
    if (currentPlan && currentPlan !== 'free') {
      handleChangePlan(planId);
      return;
    }

    setSelectedPlan(planId);
    setProviderDialogOpen(true);
  };

  const handleChangePlan = async (planId) => {
    setCheckoutLoading(true);
    try {
      await subscriptionApi.changePlan(planId);
      toast.success('Plan changed successfully!');
      setCurrentPlan(planId);
    } catch (err) {
      toast.error(err.message || 'Failed to change plan');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleCheckout = async (provider) => {
    setProviderDialogOpen(false);
    setCheckoutLoading(true);
    try {
      const data = await subscriptionApi.initiateCheckout(selectedPlan, provider);
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error('Could not create checkout session');
      }
    } catch (err) {
      toast.error(err.message || 'Checkout failed');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const getButtonConfig = (planId) => {
    if (!currentPlan || !localStorage.getItem('token')) {
      return { label: planId === 'free' ? 'Get Started' : 'Subscribe', variant: 'default', disabled: false };
    }
    if (planId === currentPlan) {
      return { label: 'Current Plan', variant: 'outline', disabled: true };
    }
    if (PLAN_ORDER[planId] > PLAN_ORDER[currentPlan]) {
      return { label: 'Upgrade', variant: 'default', disabled: false };
    }
    return { label: 'Downgrade', variant: 'secondary', disabled: false };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <section className="py-16 px-4 text-center relative">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-background to-background" />
        <div className="relative z-10 max-w-3xl mx-auto">
          <h1 className="font-cinzel text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            Choose Your Plan
          </h1>
          <p className="text-muted-foreground text-base sm:text-lg">
            Unlock the full potential of your RPG campaigns
          </p>
        </div>
      </section>

      {/* Plan Cards */}
      <section className="pb-12 px-4">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {PLAN_DEFS.map((plan) => {
            const Icon = plan.icon;
            const btn = getButtonConfig(plan.id);
            const isCurrent = plan.id === currentPlan;
            const isPopular = plan.popular;

            return (
              <Card
                key={plan.id}
                className={`relative flex flex-col transition-all duration-200 ${
                  isPopular
                    ? 'ring-2 ring-primary border-primary shadow-lg scale-[1.02]'
                    : 'border-border/50 hover:border-border'
                } ${isCurrent ? 'bg-primary/[0.03]' : ''}`}
              >
                {isPopular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-primary text-primary-foreground text-xs font-medium">
                    Most Popular
                  </div>
                )}
                {isCurrent && (
                  <div className="absolute -top-3 right-4">
                    <Badge variant="secondary" className="text-xs">
                      Current Plan
                    </Badge>
                  </div>
                )}
                <CardContent className="p-6 flex flex-col flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className="h-5 w-5 text-primary" />
                    <h3 className="font-cinzel font-bold text-xl">{plan.name}</h3>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">{plan.description}</p>

                  <div className="mb-6">
                    {plan.price === 0 ? (
                      <span className="font-cinzel text-4xl font-black text-foreground">Free</span>
                    ) : (
                      <>
                        <span className="font-cinzel text-4xl font-black text-foreground">
                          {plan.price.toFixed(2)}&#8364;
                        </span>
                        <span className="text-sm text-muted-foreground">/month</span>
                      </>
                    )}
                  </div>

                  <ul className="space-y-3 mb-8 flex-1">
                    {Object.entries(plan.features).map(([key, value]) => {
                      const included = value !== false;
                      return (
                        <li key={key} className="flex items-start gap-2.5 text-sm">
                          {included ? (
                            <Check className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                          ) : (
                            <X className="h-4 w-4 text-muted-foreground/40 flex-shrink-0 mt-0.5" />
                          )}
                          <span className={included ? 'text-muted-foreground' : 'text-muted-foreground/40'}>
                            {typeof value === 'string' ? value : FEATURE_LABELS[key]}
                          </span>
                        </li>
                      );
                    })}
                  </ul>

                  <Button
                    className={isPopular ? 'fantasy-button w-full' : 'w-full'}
                    variant={isPopular && !btn.disabled ? 'default' : btn.variant}
                    size="lg"
                    disabled={btn.disabled || checkoutLoading}
                    onClick={() => handleSelectPlan(plan.id)}
                  >
                    {checkoutLoading && selectedPlan === plan.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      btn.label
                    )}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Feature Comparison Table */}
      <section className="pb-20 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-cinzel text-2xl font-bold text-center mb-8">
            Feature Comparison
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3 font-medium text-muted-foreground">Feature</th>
                  {PLAN_DEFS.map((p) => (
                    <th key={p.id} className="text-center p-3 font-semibold">
                      {p.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.keys(FEATURE_LABELS).map((key) => (
                  <tr key={key} className="border-b border-border/50">
                    <td className="p-3 text-muted-foreground">{FEATURE_LABELS[key]}</td>
                    {PLAN_DEFS.map((plan) => {
                      const val = plan.features[key];
                      return (
                        <td key={plan.id} className="text-center p-3">
                          {val === false ? (
                            <X className="h-4 w-4 text-muted-foreground/40 mx-auto" />
                          ) : typeof val === 'string' ? (
                            <span className="text-foreground">{val}</span>
                          ) : (
                            <Check className="h-4 w-4 text-green-500 mx-auto" />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Provider Selection Dialog */}
      <Dialog open={providerDialogOpen} onOpenChange={setProviderDialogOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Choose Payment Method</DialogTitle>
            <DialogDescription>
              Select how you would like to pay for your subscription.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-4">
            <Button
              variant="outline"
              className="w-full justify-start gap-3 h-14 text-left"
              onClick={() => handleCheckout('stripe')}
            >
              <CreditCard className="h-5 w-5 text-primary" />
              <div>
                <div className="font-medium">Credit / Debit Card</div>
                <div className="text-xs text-muted-foreground">Visa, Mastercard, AMEX...</div>
              </div>
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start gap-3 h-14 text-left"
              onClick={() => handleCheckout('paypal')}
            >
              <svg className="h-5 w-5 text-[#003087]" viewBox="0 0 24 24" fill="currentColor">
                <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797H9.603c-.564 0-1.04.408-1.13.964L7.076 21.337z" />
              </svg>
              <div>
                <div className="font-medium">PayPal</div>
                <div className="text-xs text-muted-foreground">Pay with your PayPal account</div>
              </div>
            </Button>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setProviderDialogOpen(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
